/**
 * `?showcase=combat` — runs the tutorial bout standalone over the ShowcaseScene, shows the outcome,
 * and lets critics replay it with a different seed (确认 = 再来一次). Seeds are `?seed=` + replay index.
 */
import Phaser from 'phaser';
import { FONT_BODY, FONT_TITLE, GAME_H, GAME_W } from '@/core/config';
import { ctxOf } from '@/core/context';
import { bus, Events } from '@/core/events';
import type { BattleOutcome } from '@/core/types/contracts';
import { installInputMap } from '@/characters';
import { runBattle, battleSeed } from './runBattle';

const OUTCOME_TEXT: Record<BattleOutcome, string> = { goal: '招式学成（goal）', rounds: '日头偏西（rounds）', downed: '师父收剑（downed）' };

export function combatShowcase(scene: Phaser.Scene): void {
  scene.cameras.main.setBackgroundColor('#1a120c');
  const title = scene.add.text(GAME_W / 2, 120, '战斗展示 · 师徒切磋', { fontFamily: FONT_TITLE, fontSize: '22px', color: '#e2c48c' }).setOrigin(0.5);
  const info = scene.add.text(GAME_W / 2, 160, '', { fontFamily: FONT_BODY, fontSize: '14px', color: '#f1e3c2', align: 'center', lineSpacing: 6 }).setOrigin(0.5, 0);
  const hint = scene.add.text(GAME_W / 2, GAME_H - 40, '按 确认 再来一次（换一个种子）', { fontFamily: FONT_BODY, fontSize: '12px', color: '#c9b89a' }).setOrigin(0.5).setVisible(false);
  let replay = 0;
  let running = false;
  const baseSeed = (() => { try { return ctxOf(scene).seed; } catch { return 1157; } })();

  const play = async () => {
    if (running) return;
    running = true;
    hint.setVisible(false);
    const seed = (battleSeed(baseSeed, 'sword-training') + replay) >>> 0;
    info.setText([`第 ${replay + 1} 局 · 种子 ${seed}`, '战斗进行中…']);
    const outcome = await runBattle(scene, 'sword-training', { seed, standalone: true });
    running = false;
    replay++;
    info.setText([`第 ${replay} 局 · 种子 ${seed}`, `结果：${OUTCOME_TEXT[outcome]}`, '', '↑↓ 选择指令 · Enter/Space/Z 确认 · 点击/触摸亦可', '观察 不耗回合；防御 减半伤害并回复技力', '目标：使出「猎手直刺」一次并站到最后（3–5 回合，不会 Game Over）']);
    hint.setVisible(true);
  };

  // Keyboard → bus layer for this scene (paused while the bout runs, so no double confirms).
  installInputMap(scene);
  const onConfirm = () => { if (!running) void play(); };
  bus.onTyped(Events.INPUT_CONFIRM, onConfirm);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => { bus.offTyped(Events.INPUT_CONFIRM, onConfirm); title.destroy(); });
  void play();
}
